/**
 * App Header Component
 * Top header bar with branding, mobile menu toggle, and sidebar collapse
 */

import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { useNavigate } from 'react-router-dom';
import { Menu, X, PanelRight, LogOut } from 'lucide-react';
import { Button } from './ui/button';
import bismillahSrc from '@/images/bismillah.png';

interface AppHeaderProps {
  onMobileMenuToggle?: () => void;
  isMobileMenuOpen?: boolean;
  onCollapseToggle?: () => void;
  isCollapsed?: boolean;
}

export function AppHeader({
  onMobileMenuToggle,
  isMobileMenuOpen,
  onCollapseToggle,
  isCollapsed,
}: AppHeaderProps = {}) {
  const { user, logout } = useAuthStore();
  const { showToast } = useUIStore();
  const navigate = useNavigate();

  if (!user) return null;

  const handleLogout = async () => {
    try {
      await logout();
      showToast('Logged out successfully', 'success');
      navigate('/login', { replace: true });
    } catch (error) {
      showToast('Failed to logout', 'error');
    }
  };

  return (
    <header className="border-b border-border/50 bg-background/60 backdrop-blur-md">
      <div className="flex h-[68px] items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {/* Mobile menu button (mobile only) */}
          {onMobileMenuToggle && (
            <button
              onClick={onMobileMenuToggle}
              className="md:hidden p-2 -ml-2 rounded-lg hover:bg-accent transition-colors flex-shrink-0"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          )}

          {/* Collapse button (tablet/desktop only) - hide when collapsed */}
          {onCollapseToggle && !isCollapsed && (
            <button
              onClick={onCollapseToggle}
              className="hidden md:flex p-2 -ml-2 rounded-lg hover:bg-accent transition-colors flex-shrink-0"
              aria-label="Collapse sidebar"
            >
              <PanelRight className="h-5 w-5" />
            </button>
          )}

          {/* Branding: logo + full title */}
          <img
            src={bismillahSrc}
            alt="FMB"
            className="h-7 sm:h-8 w-auto flex-shrink-0 dark-logo-filter"
          />
          <div className="min-w-0">
            <h1 className="text-sm sm:text-base font-semibold truncate leading-tight">
              Faiz-ul-Mawaid al-Burhaniyah
            </h1>
            <p className="text-xs text-muted-foreground hidden sm:block">San Jose</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Thaali number - always visible with nice color */}
          {user.thaaliNumber && (
            <div className="flex flex-col items-end px-2 py-1 rounded-md bg-primary/10">
              <span className="text-sm font-medium text-primary leading-tight">
                #{user.thaaliNumber}
              </span>
              {user.name && (
                <span className="text-xs text-muted-foreground leading-tight max-w-[120px] truncate">
                  {user.name}
                </span>
              )}
            </div>
          )}

          {/* Logout button - desktop only */}
          <Button
            variant="ghost"
            size="sm"
            className="hidden md:flex gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4" />
            Logout
          </Button>
        </div>
      </div>
    </header>
  );
}
